import memoizeOne from 'memoize-one';
import type { IssueId } from '@atlassian/jira-shared-types';
import type { Hash } from '@atlassian/jira-software-roadmap-model';
import {
	BASE_LEVEL_ITEM_HEIGHT,
	BEFORE,
	DEFAULT_ITEM_HEIGHT,
	INSIDE,
} from '@atlassian/jira-software-roadmap-timeline-table';
import type {
	CreateItemAnchor,
	ExpandedItems,
} from '@atlassian/jira-software-roadmap-timeline-table/src/types';
import { isCreateBeforeItem, isCreateInsideItem, isRootDepth } from '../item-level';

/**
 * Calculates and returns the offset positions for each item in a timeline, optimising performance through memoization.
 *
 * @param {IssueId[]} itemIds - An array of issue IDs to calculate offsets for.
 * @param {Hash<IssueId[]>} childIdsHash - A map of parent issue IDs to their child issue IDs.
 * @param {ExpandedItems} expandedItems - Indicates which items are expanded to correctly calculate offsets.
 * @param {CreateItemAnchor} createItemAnchor - The reference point for item creation.
 *
 * @returns {Hash<number>} A map of issue IDs to their calculated offset positions.
 */
export const getItemOffsetPositions = memoizeOne(
	(
		itemIds: IssueId[],
		childIdsHash: Hash<IssueId[]>,
		expandedItems: ExpandedItems,
		createItemAnchor: CreateItemAnchor,
	): Hash<number> => {
		const positions: Hash<number> = {};
		let cumulativeOffset = 0;

		const calculateOffsetPositions = (id: IssueId, depth: number) => {
			if (isCreateBeforeItem(createItemAnchor, id)) {
				cumulativeOffset += DEFAULT_ITEM_HEIGHT;
			}

			const itemHeight = isRootDepth(depth) ? DEFAULT_ITEM_HEIGHT : BASE_LEVEL_ITEM_HEIGHT;
			positions[id] = cumulativeOffset + itemHeight / 2;

			cumulativeOffset += itemHeight;
			const childIds = childIdsHash[id];
			if (childIds && expandedItems[id]) {
				childIds.forEach((childId) => calculateOffsetPositions(childId, depth + 1));
			}

			if (isCreateInsideItem(createItemAnchor, id)) {
				cumulativeOffset += DEFAULT_ITEM_HEIGHT;
			}
		};

		itemIds.forEach((id) => calculateOffsetPositions(id, 0));
		return positions;
	},
);

/** @todo Delete the following during jwm.visual-hierarchy-milestone-1 cleanup */

type ItemOffset = {
	id: string;
	position: number;
	height: number;
};

export const getItemsOffsets = (
	itemHeight: number,
	itemIds: IssueId[],
	childIdsHash: Hash<IssueId[]> = {},
	expandedItems: ExpandedItems = {},
	createItemAnchor: CreateItemAnchor,
): ItemOffset[] => {
	const result: Array<ItemOffset> = [];
	let prevItemOffset = 0;

	itemIds.forEach((id: IssueId) => {
		const childIds = childIdsHash[id] || [];
		let childHeight = expandedItems[id] ? childIds.length * BASE_LEVEL_ITEM_HEIGHT : 0;
		let currentOffset = prevItemOffset;

		/* Include offsets caused by the create item.
		 * The create item is always the same height (itemHeight) even when creating children.
		 */
		if (createItemAnchor) {
			const createBeforeItem =
				createItemAnchor.position === BEFORE && createItemAnchor.beforeId === id;

			const createBeforeChildItem =
				createItemAnchor.position === BEFORE &&
				createItemAnchor.beforeId === id &&
				createItemAnchor.parentId !== undefined;

			const createInsideItem =
				createItemAnchor.position === INSIDE && createItemAnchor.parentId === id;

			if (createBeforeItem) {
				currentOffset += itemHeight;
			}

			if (createInsideItem || createBeforeChildItem) {
				childHeight += itemHeight;
			}
		}

		const totalHeight = itemHeight + childHeight;

		result.push({
			id: String(id),
			position: currentOffset + itemHeight / 2,
			height: itemHeight,
		});

		// if parent is expanded, include children in the list
		if (expandedItems[id]) {
			let childPrevItemOffset = currentOffset + itemHeight;

			childIds.forEach((childId: IssueId) => {
				if (
					createItemAnchor &&
					// @ts-expect-error - TS2339 - Property 'parentId' does not exist on type 'LastAnchor | RelativeAnchor'.
					createItemAnchor.parentId &&
					createItemAnchor.position === BEFORE &&
					createItemAnchor.beforeId === childId
				) {
					childPrevItemOffset += itemHeight;
					currentOffset += itemHeight;
				}
				result.push({
					id: String(childId),
					position: childPrevItemOffset + BASE_LEVEL_ITEM_HEIGHT / 2,
					height: BASE_LEVEL_ITEM_HEIGHT,
				});

				childPrevItemOffset += BASE_LEVEL_ITEM_HEIGHT;
			});
		}
		prevItemOffset = currentOffset + totalHeight;
	});

	return result;
};

const parsePositions = (items: ItemOffset[]): Hash<number> =>
	items.reduce<Hash<number>>((acc, { id, position }: ItemOffset) => {
		acc[id] = position;
		return acc;
	}, {});

export const getItemOffsetPositionsOld = (
	itemHeight: number,
	itemIds: IssueId[],
	childIdsHash?: Hash<IssueId[]>,
	expandedItems?: ExpandedItems,
	// @ts-expect-error - TS1016 - A required parameter cannot follow an optional parameter.
	createItemAnchor: CreateItemAnchor,
): Hash<number> =>
	parsePositions(
		getItemsOffsets(itemHeight, itemIds, childIdsHash, expandedItems, createItemAnchor),
	);
